import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, Truck, RotateCcw, Sun } from "lucide-react";
import { api, useSEO } from "@/lib/api";
import { MaskReveal, Reveal } from "@/components/Reveal";
import { ProductCard } from "@/components/ProductCard";

const HERO_IMAGE = "/assets/vyron/hero-5.jpeg";  // Un-cropped uploaded hero banner

const FEATURES = [
  [Sun, "Polarized", "UV400 Lenses"],
  [Truck, "Free Shipping", "Across India"],
  [ShieldCheck, "1 Year", "Warranty"],
  [RotateCcw, "Easy 7-Day", "Returns"],
];

const COLLECTION_TILES = [
  { slug: "men", name: "Men", img: "/assets/vyron/gold-3.jpeg" },
  { slug: "women", name: "Women", img: "/assets/vyron/taupe-d.jpeg" },
  { slug: "best-sellers", name: "Best Sellers", img: "/assets/vyron/noir-5.jpeg" },
  { slug: "new-arrivals", name: "New Arrivals", img: "/assets/vyron/tort-5.jpeg" },
];

export default function Home() {
  useSEO(
    "Premium Polarized Eyewear",
    "VYRON — Premium polarized eyewear crafted for those who wear the vision and own the style. UV400 protection, precision hinges, luxury packaging.",
    { image: HERO_IMAGE }
  );
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/products?featured=true").then((r) => setFeatured(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page" className="bg-black text-white">
      {/* ============ EDITORIAL HERO ============ */}
      <section className="relative bg-black overflow-hidden" data-testid="hero">
        <div className="relative w-full min-h-[calc(100vh-114px)] grid lg:grid-cols-12">
          {/* Text left */}
          <div className="relative z-10 lg:col-span-5 flex flex-col justify-center px-6 md:px-12 lg:pl-16 pt-14 pb-10 md:pt-20 md:pb-16 lg:py-0">
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
              className="flex items-center gap-3 mb-6">
              <span className="h-px w-8 bg-gold" />
              <p className="text-[11px] md:text-xs uppercase tracking-[0.42em] text-gold">
                Premium Polarized Eyewear
              </p>
              <span className="h-px w-8 bg-gold" />
            </motion.div>
            <h1 className="font-display text-6xl sm:text-7xl md:text-[9rem] leading-[0.9] tracking-[0.02em] mb-8">
              <MaskReveal delay={0.15}>VYRON</MaskReveal>
            </h1>
            <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.4 }}
              className="text-xl md:text-2xl tracking-[0.08em] text-white mb-10 uppercase">
              Wear the <span className="text-gold">vision</span>. Own the <span className="text-gold">style</span>.
            </motion.p>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6, duration: 0.9 }}
              className="flex flex-wrap items-center gap-3">
              <Link to="/shop" data-testid="hero-shop-btn"
                className="group inline-flex items-center gap-3 bg-gold text-black rounded-none px-10 py-4 uppercase text-[11px] tracking-[0.3em] font-medium hover:brightness-110 transition-all">
                Shop Now
                <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link to="/collections" data-testid="hero-explore-btn"
                className="inline-flex items-center gap-3 rounded-none px-10 py-4 uppercase text-[11px] tracking-[0.3em] border border-gold text-gold hover:bg-gold hover:text-black transition-colors">
                Explore Collection
              </Link>
            </motion.div>
          </div>

          {/* Hero image right */}
          <div className="relative lg:col-span-7 min-h-[54vh] md:min-h-[64vh] lg:min-h-full">
            <motion.img
              src={HERO_IMAGE}
              alt="VYRON — Premium Polarized Eyewear"
              fetchPriority="high"
              initial={{ opacity: 0, scale: 1.03 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.3, ease: [0.22, 1, 0.36, 1] }}
              className="absolute inset-0 w-full h-full object-contain object-center"
            />
            {/* Left fade for text legibility */}
            <div className="absolute inset-y-0 left-0 w-1/3 bg-gradient-to-r from-black via-black/60 to-transparent pointer-events-none hidden lg:block" />
          </div>
        </div>
      </section>

      {/* ============ FEATURE BAR ============ */}
      <section className="bg-black border-y border-white/10" data-testid="feature-strip">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-8 md:py-10 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {FEATURES.map(([Icon, t, b], i) => (
            <div key={t} className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full border border-gold/50 flex items-center justify-center shrink-0">
                <Icon size={22} strokeWidth={1.3} className="text-gold" />
              </div>
              <div className="leading-tight">
                <p className="text-sm md:text-base uppercase tracking-[0.15em] font-medium text-white">{t}</p>
                <p className="text-[11px] md:text-xs uppercase tracking-[0.15em] text-white/60 mt-1">{b}</p>
              </div>
              {i < FEATURES.length - 1 && <span className="hidden md:block ml-auto w-px h-14 bg-white/10" />}
            </div>
          ))}
        </div>
      </section>

      {/* ============ SHOP BY COLLECTION ============ */}
      <section className="bg-black" data-testid="shop-collections">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-16 md:py-20">
          <div className="text-center mb-10 md:mb-12">
            <div className="inline-flex items-center gap-4 mb-4">
              <span className="h-px w-10 bg-gold" />
              <p className="text-xs uppercase tracking-[0.32em] text-gold">Shop by Collection</p>
              <span className="h-px w-10 bg-gold" />
            </div>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {COLLECTION_TILES.map((c, i) => (
              <Reveal key={c.slug} delay={i * 0.06}>
                <Link to={`/collection/${c.slug}`} data-testid={`collection-tile-${c.slug}`}
                  className="group relative block aspect-[4/5] overflow-hidden rounded-none bg-[#111] border border-white/5 hover:border-gold/40 transition-colors">
                  <img src={c.img} alt={c.name} loading="lazy"
                    className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 p-5 md:p-7 text-white">
                    <h3 className="font-display text-2xl md:text-3xl uppercase tracking-[0.08em] leading-none">{c.name}</h3>
                    <p className="mt-3 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-gold border-b border-gold/60 pb-1">
                      Explore Now
                    </p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ THE COLLECTION ============ */}
      {featured.length > 0 && (
        <section className="bg-white text-black" data-testid="home-collection">
          <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28">
            <div className="flex items-end justify-between mb-10 md:mb-14 flex-wrap gap-4">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-gold mb-4">The Collection</p>
                <h2 className="font-display text-4xl md:text-6xl tracking-tight leading-[0.95]">
                  <MaskReveal>Built for those who see differently.</MaskReveal>
                </h2>
              </div>
              <Link to="/shop" data-testid="home-shop-all"
                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] border border-black/25 rounded-full px-6 py-3 hover:bg-black hover:text-white transition-colors">
                Shop All <ArrowRight size={14} />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="home-collection-grid">
              {featured.map((p, i) => (
                <ProductCard key={p.slug} product={p} index={i} />
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
